import React, { createContext, useContext, useMemo, useReducer, useCallback, useEffect, useRef } from "react";
import { supabase, getCachedAccessToken } from '@lib/supabaseClient';
import { localSettings } from '@utils/localSettings';
import { getApiBaseUrl } from '@utils/apiUrl';
import { validatePasswordPolicy } from '@utils/passwordPolicy';
import { toast } from 'react-toastify';
import { getLogger } from '@providers/loggerProvider';

const AuthContext = createContext();

const SESSION_ENDED_MESSAGE = 'Your session has ended. Please sign in again.';
const logger = getLogger('AuthProvider');

export const isAppAuthFailure = (response) => response?.status === 401
    && ['AUTH_REQUIRED', 'API_ACCESS_TOKEN_EXPIRED'].includes(response.meta?.error?.code);

const _initialState = { user: null, isLoading: false, error: null };

// Strip BrickLink credential secrets from the auth user payload before
// storing in localStorage. The settings page fetches the full record on
// demand via getUserSettings; nothing in the auth context needs these.
const BRICKLINK_SECRET_FIELDS = [
    'bricklink_api_consumer_key',
    'bricklink_api_consumer_secret',
    'bricklink_api_token',
    'bricklink_api_token_secret'
];

const stripAuthSecrets = (userState) => {
    if (!userState || typeof userState !== 'object') return userState;
    const sanitized = { ...userState };
    for (const field of BRICKLINK_SECRET_FIELDS) {
        delete sanitized[field];
    }
    return sanitized;
};

const createInitialState = () => {
    // Remove stale token keys left by the old token management system.
    // These keys are no longer used — Supabase SDK manages its own session.
    localStorage.removeItem('bb_setting_accessToken');
    localStorage.removeItem('bb_setting_refreshToken');

    // During password recovery, skip restoring the stored user so isAuthenticated
    // stays false and PublicRoutes render (allowing /reset-password to be reached).
    const isRecoveryFlow = window.location.hash.includes('type=recovery');
    const storedUser = isRecoveryFlow ? null : localSettings.get('user', null);
    const cachedUser = Array.isArray(storedUser) ? storedUser[0] : storedUser;
    // A profile cached by an older build can lack bricklink_connected, which the
    // header and BrickLink routes require. Drop it so the session bootstrap
    // reloads a current profile from /api/users/me.
    const user = typeof cachedUser?.bricklink_connected === 'boolean' ? cachedUser : null;
    return { user, isLoading: false, error: null };
};

const authReducer = (state, action) => {
    switch (action.type) {
        case 'SET_USER':    return { ...state, user: action.payload, error: null };
        case 'CLEAR_USER':  return { ...state, user: null, error: null };
        case 'SET_LOADING': return { ...state, isLoading: action.payload };
        case 'SET_ERROR':   return { ...state, error: action.payload };
        default:            return state;
    }
};

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, null, createInitialState);
    const profileUserIdRef = useRef(state.user?.id ?? null);
    const activeSessionUserIdRef = useRef(null);
    const profileLoadVersionRef = useRef(0);
    // Access token whose profile is already loaded. The getSession() bootstrap
    // and the SIGNED_IN event both fire for one session; only fetch once per token.
    const loadedAccessTokenRef = useRef(null);

    const clearProfileState = useCallback(() => {
        profileUserIdRef.current = null;
        loadedAccessTokenRef.current = null;
        dispatch({ type: 'CLEAR_USER' });
        localSettings.remove('user');
    }, []);

    const endSession = useCallback(async (accessToken) => {
        if (typeof accessToken !== 'string' || !accessToken) {
            throw new Error('Ending a session requires its access token');
        }
        if (accessToken !== getCachedAccessToken()) return;
        if (!profileUserIdRef.current && !activeSessionUserIdRef.current) return;
        const hadProfile = !!profileUserIdRef.current;
        activeSessionUserIdRef.current = null;
        profileLoadVersionRef.current += 1;
        clearProfileState();
        if (hadProfile) toast.info(SESSION_ENDED_MESSAGE, { toastId: 'session-ended' });
        try {
            const { error } = await supabase.auth.signOut({ scope: 'local' });
            if (error) throw error;
        } catch (error) {
            logger.error('Local session sign-out failed', { message: error.message });
        }
    }, [clearProfileState]);

    const loadUserProfile = useCallback(async (accessToken, expectedUserId = null) => {
        // Accept an access token directly to avoid a redundant getSession() call
        // that competes for the Navigator Lock and causes "Lock broken" errors.
        let token = accessToken;
        if (!token) {
            const { data: { session } } = await supabase.auth.getSession();
            token = session?.access_token;
        }
        if (!token) return;
        if (token === loadedAccessTokenRef.current) return;
        // Version only real loads: a skipped duplicate for the token already in
        // flight must not invalidate that load's response.
        const loadVersion = ++profileLoadVersionRef.current;
        // Mark the token before the request so a concurrent load for it is skipped.
        // A failed load clears the mark so the next SIGNED_IN for it retries.
        loadedAccessTokenRef.current = token;
        // Session and load-version guards must follow the await because they can change while the request is pending.
        // react-doctor-disable-next-line react-doctor/async-defer-await
        const { response, data } = await fetch(`${getApiBaseUrl()}/api/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(async (response) => ({
            response,
            data: await response.json()
        })).catch((error) => {
            if (loadVersion === profileLoadVersionRef.current) loadedAccessTokenRef.current = null;
            throw error;
        });
        if (loadVersion !== profileLoadVersionRef.current) return;
        if (
            expectedUserId
            && activeSessionUserIdRef.current
            && expectedUserId !== activeSessionUserIdRef.current
        ) return;
        if (!data.success) loadedAccessTokenRef.current = null;
        if (isAppAuthFailure({ status: response.status, meta: data.meta })) {
            // SIGNED_IN subscribers hold the SDK lock. Clear locally now, but
            // let the subscriber finish before SDK sign-out acquires that lock.
            void endSession(token);
            return;
        }
        if (data.success) {
            const rawUser = Array.isArray(data.data) ? data.data[0] : data.data;

            // A confirmation/magic-link can replace an existing Supabase session
            // while an earlier /users/me request is still in flight. Never let
            // that stale response overwrite the profile for the newer session.
            if (expectedUserId && rawUser?.id && rawUser.id !== expectedUserId) return;

            const userState = stripAuthSecrets(rawUser);
            profileUserIdRef.current = userState?.id ?? null;
            dispatch({ type: 'SET_USER', payload: userState });
            localSettings.set('user', userState);
        }
    }, [endSession]);

    useEffect(() => {
        const isRecoveryFlow = window.location.hash.includes('type=recovery');

        // Restore session on page load (skip during recovery so user stays unauthenticated)
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!session || isRecoveryFlow) return;

            const sessionUserId = session.user?.id ?? null;

            // If an auth event already established a different session, this
            // initial getSession result is stale and must not start a profile load.
            if (
                activeSessionUserIdRef.current
                && sessionUserId
                && activeSessionUserIdRef.current !== sessionUserId
            ) return;

            activeSessionUserIdRef.current = sessionUserId;

            // Cached profile state belongs to a different account. Clear it
            // immediately rather than rendering the previous user's identity
            // while the current profile request completes.
            if (
                sessionUserId
                && profileUserIdRef.current
                && profileUserIdRef.current !== sessionUserId
            ) {
                clearProfileState();
            }

            loadUserProfile(session.access_token, sessionUserId);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                if (event === 'PASSWORD_RECOVERY') {
                    return;
                }
                if (event === 'SIGNED_IN' && session && !isRecoveryFlow) {
                    const sessionUserId = session.user?.id ?? null;
                    activeSessionUserIdRef.current = sessionUserId;

                    if (
                        sessionUserId
                        && profileUserIdRef.current
                        && profileUserIdRef.current !== sessionUserId
                    ) {
                        clearProfileState();
                    }

                    await loadUserProfile(session.access_token, sessionUserId);
                }
                if (event === 'SIGNED_OUT') {
                    // logout() clears the profile before signing out, so a
                    // profile still loaded here means Supabase ended the
                    // session itself (its refresh token was revoked or expired).
                    if (profileUserIdRef.current) {
                        toast.info(SESSION_ENDED_MESSAGE, { toastId: 'session-ended' });
                    }
                    activeSessionUserIdRef.current = null;
                    profileLoadVersionRef.current += 1;
                    clearProfileState();
                }
            }
        );

        // Cross-tab user state sync: when another tab writes bb_setting_user
        // (e.g. via setUser after a profile update), reflect that change here
        // instead of leaving this tab with stale data.
        const onStorage = (event) => {
            if (event.key !== 'bb_setting_user') return;
            if (event.newValue === null) {
                profileUserIdRef.current = null;
                dispatch({ type: 'CLEAR_USER' });
                return;
            }
            try {
                const parsed = JSON.parse(event.newValue);
                const next = Array.isArray(parsed) ? parsed[0] : parsed;
                if (next) {
                    profileUserIdRef.current = next.id ?? null;
                    dispatch({ type: 'SET_USER', payload: next });
                }
            } catch {
                // Corrupt payload — ignore; the next API refresh will recover.
            }
        };
        window.addEventListener('storage', onStorage);

        return () => {
            subscription.unsubscribe();
            window.removeEventListener('storage', onStorage);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const login = useCallback(async (email_address, password) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const { error } = await supabase.auth.signInWithPassword({ email: email_address, password });
            if (error) throw error;
            // onAuthStateChange SIGNED_IN fires and calls loadUserProfile
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    const register = useCallback(async ({ email_address, password }) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const passwordError = validatePasswordPolicy(password);
            if (passwordError) throw new Error(passwordError);

            const { data, error } = await supabase.auth.signUp({
                email: email_address,
                password,
                options: { emailRedirectTo: `${window.location.origin}/app/home` }
            });
            if (error) throw error;
            // Return data so caller can check user.identities for duplicate detection
            return data;
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    const logout = useCallback(async () => {
        activeSessionUserIdRef.current = null;
        profileLoadVersionRef.current += 1;
        clearProfileState();
        try {
            await supabase.auth.signOut();
        } catch {
            // If signOut fails or hangs, local state is already cleared above
        }
    }, [clearProfileState]);

    const resetPassword = useCallback(async (email) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`
        });
        if (error) throw error;
    }, []);

    const contextValue = useMemo(() => ({
        user: state.user,
        isLoading: state.isLoading,
        error: state.error,
        login,
        logout,
        endSession,
        register,
        resetPassword,
        isAuthenticated: !!state.user,
        hasRole: (role) => state.user?.role === role,
        setUser: (userData) => {
            const sanitized = stripAuthSecrets(userData);
            profileUserIdRef.current = sanitized?.id ?? null;
            dispatch({ type: 'SET_USER', payload: sanitized });
            localSettings.set('user', sanitized);
        }
    }), [state.user, state.isLoading, state.error, login, logout, endSession, register, resetPassword]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};

export default AuthProvider;
